import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Icon, Input, Row, Select, Table, message } from 'antd';
import { withRouter } from 'react-router-dom';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import { MONTH_FORMAT } from 'utils/constants';
import { listExpenses, listServicePackages } from '../api/ownerReporting';
import { loadHostProperties, loadHostUnits, loadUnitsForProperty } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { readPageFilters, rowsForSelectedMonth, toMonthDate, useSyncPageFilters } from './pageFilters';
import BulkExpensesUploadModal from '../components/BulkExpensesUploadModal';
import ExpensesFormDrawer from '../components/ExpensesFormDrawer';
import { BULK_EXPENSE_COLUMNS } from '../components/expensesByUnit';
import { downloadCsvFile } from './revenueCsv';
import { mapExpensesToCsvRows } from './expensesCsv';
import tableStyles from './tableOverflow.module.css';

const LUMPSUM_KEYS = [
  'cleaning',
  'electricity',
  'water',
  'internet',
  'laundry',
  'service',
  'checkIn',
  'checkOut',
];

const normalizeId = id => (id && id._id ? String(id._id) : String(id));

const normalizePackageId = pkg => {
  if (!pkg) return undefined;
  if (typeof pkg === 'object') return String(pkg._id || pkg);
  return String(pkg);
};

const formatMoney = n => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumLineItems = rows => (rows || []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

const sumExpensesLumpsum = expenses => {
  if (!expenses || typeof expenses !== 'object') return 0;
  const scalars = LUMPSUM_KEYS.reduce((sum, key) => sum + (Number(expenses[key]) || 0), 0);
  const othersScalar = Number(expenses.others) || 0;
  const othersHost = sumLineItems(expenses.othersByHost);
  const othersOwner = sumLineItems(expenses.othersByOwner);
  const toiletry = sumLineItems(expenses.toiletry);
  // Prefer line-item totals for others when present; otherwise use scalar others.
  const othersTotal = othersHost + othersOwner > 0 ? othersHost + othersOwner : othersScalar;
  return scalars + othersTotal + toiletry;
};

const sumMonthly = monthly =>
  LUMPSUM_KEYS.reduce((sum, key) => sum + (Number(monthly && monthly[key]) || 0), 0);

const packageNameOf = (row, packageNameById) =>
  (packageNameById && packageNameById[normalizePackageId(row && row.servicePackage)]) ||
  normalizePackageId(row && row.servicePackage) ||
  '';

const ExpensesPage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [properties, setProperties] = useState([]);
  const [hostUnits, setHostUnits] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [propertyId, setPropertyId] = useState(() => readPageFilters(location && location.search).propertyId);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [bulkVisible, setBulkVisible] = useState(false);
  const [monthDate, setMonthDate] = useState(() => readPageFilters(location && location.search).monthDate);
  const [year, setYear] = useState(() => readPageFilters(location && location.search).monthDate.year());
  const [month, setMonth] = useState(() => readPageFilters(location && location.search).monthDate.month() + 1);
  const [packageQuery, setPackageQuery] = useState('');

  const packageNameById = useMemo(() => {
    const map = {};
    (packages || []).forEach(p => {
      map[String(p._id)] = p.name;
    });
    return map;
  }, [packages]);

  const propertyUnitIds = useMemo(() => {
    if (!propertyId) return null;
    return new Set(
      (hostUnits || [])
        .filter(u => {
          const property = u.roomType && u.roomType.property;
          const pid = property && property._id ? property._id : property;
          return String(pid) === String(propertyId);
        })
        .map(u => String(u._id))
    );
  }, [hostUnits, propertyId]);

  const packagesForProperty = useMemo(() => {
    if (!propertyId) return packages || [];
    const unitIds = propertyUnitIds || new Set();
    return (packages || []).filter(pkg => (pkg.units || []).some(uid => unitIds.has(normalizeId(uid))));
  }, [packages, propertyId, propertyUnitIds]);

  const packageIdsForProperty = useMemo(
    () => new Set((packagesForProperty || []).map(p => String(p._id))),
    [packagesForProperty]
  );

  const filteredRows = useMemo(() => {
    if (!propertyId) return rows || [];
    return (rows || []).filter(r => packageIdsForProperty.has(normalizePackageId(r.servicePackage)));
  }, [rows, propertyId, packageIdsForProperty]);

  const monthRows = useMemo(
    () => rowsForSelectedMonth(filteredRows, year, month),
    [filteredRows, year, month]
  );

  const displayRows = useMemo(() => {
    const q = String(packageQuery || '')
      .trim()
      .toLowerCase();
    if (!q) return monthRows;
    return (monthRows || []).filter(r =>
      String(packageNameOf(r, packageNameById))
        .toLowerCase()
        .includes(q)
    );
  }, [monthRows, packageQuery, packageNameById]);

  const monthSummary = useMemo(() => {
    return displayRows.reduce(
      (acc, r) => {
        const exp = r.expenses || {};
        LUMPSUM_KEYS.forEach(key => {
          acc[key] += Number(exp[key]) || 0;
          acc[key] += Number(r.monthly && r.monthly[key]) || 0;
        });
        const othersHost = sumLineItems(exp.othersByHost);
        const othersOwner = sumLineItems(exp.othersByOwner);
        acc.others += othersHost + othersOwner > 0 ? othersHost + othersOwner : Number(exp.others) || 0;
        acc.toiletry += sumLineItems(exp.toiletry);
        return acc;
      },
      {
        cleaning: 0,
        electricity: 0,
        water: 0,
        internet: 0,
        laundry: 0,
        service: 0,
        checkIn: 0,
        checkOut: 0,
        others: 0,
        toiletry: 0,
      }
    );
  }, [displayRows]);

  const monthGrandTotal = useMemo(
    () =>
      LUMPSUM_KEYS.reduce((sum, key) => sum + (Number(monthSummary[key]) || 0), 0) +
      (Number(monthSummary.others) || 0) +
      (Number(monthSummary.toiletry) || 0),
    [monthSummary]
  );

  const load = async (loadYear = year, loadMonth = month) => {
    if (!hostId) return;
    setLoading(true);
    setPropertiesLoading(true);
    try {
      const [exp, pkgs, props, units] = await Promise.all([
        listExpenses(hostId, { year: loadYear, month: loadMonth }),
        listServicePackages(hostId),
        loadHostProperties(hostId),
        loadHostUnits(hostId),
      ]);
      setRows(exp || []);
      setPackages(pkgs || []);
      const propsList = props || [];
      setProperties(propsList);
      setHostUnits(units || []);
      setPropertyId(prev => {
        if (prev && propsList.some(p => String(p._id) === String(prev))) return prev;
        return propsList[0] ? String(propsList[0]._id) : undefined;
      });
    } catch (e) {
      message.error('Failed to load expenses');
    } finally {
      setLoading(false);
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId]);

  useEffect(() => {
    if (!propertyId) {
      setPropertyUnits([]);
      return undefined;
    }
    let cancelled = false;
    loadUnitsForProperty(propertyId).then(list => {
      if (!cancelled) setPropertyUnits(list || []);
    });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useSyncPageFilters(history, location, {
    property: propertyId,
    date: monthDate.format(MONTH_FORMAT),
  });

  const onMonthChange = date => {
    const m = toMonthDate(date);
    const nextYear = m.year();
    const nextMonth = m.month() + 1;
    setMonthDate(m);
    setYear(nextYear);
    setMonth(nextMonth);
    setPackageQuery('');
    load(nextYear, nextMonth);
  };

  const openCreate = () => {
    setEditingRecord(null);
    setDrawerVisible(true);
  };

  const openEdit = row => {
    setEditingRecord(row);
    setDrawerVisible(true);
  };

  const packagesForDrawer = propertyId ? packagesForProperty : packages;

  const selectedPropertyName = useMemo(() => {
    const found = (properties || []).find(p => String(p._id) === String(propertyId));
    return (found && found.name) || '';
  }, [properties, propertyId]);

  const csvUnits = useMemo(() => (hostUnits || []).concat(propertyUnits || []), [hostUnits, propertyUnits]);

  const expensesCsvData = useMemo(
    () =>
      mapExpensesToCsvRows({
        monthRows: displayRows,
        packages: packagesForProperty,
        units: csvUnits,
        propertyName: selectedPropertyName,
      }),
    [displayRows, packagesForProperty, csvUnits, selectedPropertyName]
  );

  const expensesCsvFilename = useMemo(() => {
    const monthStr = Number(month) < 10 ? `0${Number(month)}` : String(month);
    const prefix = `${year}_${monthStr}_${selectedPropertyName}__reporting overview`;
    return `${prefix}_MONTHLY EXPENSES BREAKDOWN.csv`;
  }, [year, month, selectedPropertyName]);

  const bulkTemplateRows = useMemo(() => {
    const pkgs = packagesForProperty || [];
    const expenseByPackage = {};
    (displayRows || []).forEach(r => {
      expenseByPackage[normalizePackageId(r.servicePackage)] = r.expenses || {};
    });
    return pkgs.map(pkg => {
      const id = String(pkg._id);
      const expenses = expenseByPackage[id] || {};
      const row = {
        servicePackageId: id,
        servicePackageName: pkg.name || id,
      };
      BULK_EXPENSE_COLUMNS.forEach(col => {
        if (col.key === 'others') {
          row.others = Number(expenses.others) || sumLineItems(expenses.othersByHost) || 0;
          return;
        }
        if (col.key === 'toiletry') {
          row.toiletry = sumLineItems(expenses.toiletry);
          return;
        }
        row[col.key] = Number(expenses[col.key]) || 0;
      });
      return row;
    });
  }, [packagesForProperty, displayRows]);

  return (
    <Card title="Owner Reporting — Monthly Expenses">
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item label="Property" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
                value={propertyId}
                onChange={val => {
                  setPackageQuery('');
                  setPropertyId(val ? String(val) : undefined);
                }}
                disabled={!hostId}
                loading={propertiesLoading}
              >
                {properties.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={10} lg={9}>
            <Form.Item label="Month" style={{ marginBottom: 0 }}>
              <MonthPicker defaultValue={monthDate} onChange={onMonthChange} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={6} lg={8}>
            <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Button
                  icon="download"
                  disabled={loading || expensesCsvData.length === 0}
                  onClick={() => downloadCsvFile(expensesCsvFilename, expensesCsvData)}
                >
                  Download CSV
                </Button>
                <Button
                  type="primary"
                  onClick={() => setBulkVisible(true)}
                  disabled={!hostId || !propertyId || !bulkTemplateRows.length}
                >
                  Bulk Upload
                </Button>
                <Button onClick={openCreate} disabled={!hostId}>
                  Create Expenses
                </Button>
                <Button onClick={() => load(year, month)}>Refresh</Button>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Cleaning</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(monthSummary.cleaning)}</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Utilities</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {formatMoney(
                (monthSummary.electricity || 0) + (monthSummary.water || 0) + (monthSummary.internet || 0)
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Grand Total</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(monthGrandTotal)}</div>
          </Card>
        </Col>
      </Row>

      <div className={tableStyles.tableWrap}>
        <Table
          rowKey={r => r._id || `${normalizePackageId(r.servicePackage)}-${r.year}-${r.month}`}
          loading={loading}
          dataSource={displayRows}
          scroll={{ x: 880 }}
          columns={[
            {
              title: 'Service Package',
              key: 'servicePackage',
              width: 280,
              filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                  <Input
                    placeholder="Search service package"
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => {
                      setPackageQuery((selectedKeys && selectedKeys[0]) || '');
                      confirm();
                    }}
                    style={{ width: 220, marginBottom: 8, display: 'block' }}
                  />
                  <Button
                    type="primary"
                    icon="search"
                    size="small"
                    style={{ width: 100, marginRight: 8 }}
                    onClick={() => {
                      setPackageQuery((selectedKeys && selectedKeys[0]) || '');
                      confirm();
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    size="small"
                    style={{ width: 90 }}
                    onClick={() => {
                      setPackageQuery('');
                      if (clearFilters) clearFilters();
                      confirm();
                    }}
                  >
                    Reset
                  </Button>
                </div>
              ),
              filterIcon: filtered => (
                <Icon type="search" style={{ color: filtered || packageQuery ? '#00b9c6' : '#aaa' }} />
              ),
              render: r => {
                const name = packageNameOf(r, packageNameById) || '—';
                return <span title={name}>{name}</span>;
              },
            },
            { title: 'Year', dataIndex: 'year', width: 80 },
            { title: 'Month', dataIndex: 'month', width: 80 },
            {
              title: 'Monthly',
              width: 120,
              align: 'right',
              render: r => formatMoney(sumMonthly(r.monthly)),
            },
            {
              title: 'Entered',
              width: 120,
              align: 'right',
              render: r => formatMoney(sumExpensesLumpsum(r.expenses)),
            },
            {
              title: 'Total',
              width: 120,
              align: 'right',
              render: r => formatMoney(sumExpensesLumpsum(r.expenses) + sumMonthly(r.monthly)),
            },
            {
              title: 'Actions',
              width: 90,
              render: r => (
                <Button type="link" onClick={() => openEdit(r)} style={{ padding: 0 }}>
                  Edit
                </Button>
              ),
            },
          ]}
        />
      </div>

      <ExpensesFormDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setEditingRecord(null);
        }}
        onSaved={loadYear => load(loadYear || year, month)}
        hostId={hostId}
        packages={packagesForDrawer}
        hostUnits={hostUnits}
        initialRecord={editingRecord}
        defaultYear={year}
        defaultMonth={month}
      />

      <BulkExpensesUploadModal
        visible={bulkVisible}
        onCancel={() => setBulkVisible(false)}
        onSuccess={() => load(year, month)}
        hostId={hostId}
        year={year}
        month={month}
        templateRows={bulkTemplateRows}
        csvName={`owner-reporting-expenses-${year}-${String(month).padStart(2, '0')}`}
      />
    </Card>
  );
};

export default withRouter(ExpensesPage);
